/**
 * QuranAudioService — global, singleton-ish player core.
 *
 * Wraps a single HTMLAudioElement, exposes a queue + reactive state via a
 * tiny pub/sub. The React context (`AudioContext`) consumes this state.
 *
 * Modes: 'ayah' | 'surah' | 'hizb' | 'quran' | 'memorization' | null
 *
 * Offline-first contract:
 *   - playAyah / playSurah / playHizb / playFullQuran first ask the storage
 *     layer for a local URL. If missing, they raise a `NEEDS_DOWNLOAD` event
 *     with the suggested download scope. The UI shows the prompt.
 *   - The player itself NEVER initiates network audio playback when audioMode
 *     is 'offline_only'. The optional 'offline_first_with_streaming_fallback'
 *     mode plays the remoteUrl directly when allowed.
 */

import { audioStorage } from './audioStorage';
import { quranAudioProvider, DEFAULT_RECITER_ID } from './quranAudioProvider';
import {
  getAyahsForSurah,
  getAyahsForHizb,
  getAyahsForFullQuran,
} from './quranScopes';

const listeners = new Set();
let audio = null;        // shared HTMLAudioElement
let revoked = null;      // last blob URL we created (to revoke on next change)

const state = {
  isPlaying: false,
  isPaused: false,
  currentTrack: null,
  queue: [],
  currentIndex: 0,
  mode: null,
  reciterId: DEFAULT_RECITER_ID,
  riwaya: 'hafs',
  audioMode: 'offline_only',  // 'offline_only' | 'offline_first_with_streaming_fallback'
  progress: 0,
  duration: 0,
  error: null,
  needsDownload: null,        // { scopeType, surahNumber?, hizbNumber? }
  // Memorization repetition runtime state (null when not in memo mode)
  repeat: null, // { mode, repeatCount, pauseMs, ayahCounter, rangeCounter, startIndex, endIndex }
};

function emit() {
  listeners.forEach((l) => { try { l({ ...state }); } catch {} });
}

function ensureAudioElement() {
  if (audio) return audio;
  audio = new Audio();
  audio.preload = 'auto';
  audio.addEventListener('timeupdate', () => {
    state.progress = audio.currentTime;
    state.duration = audio.duration || state.duration;
    emit();
  });
  audio.addEventListener('ended', () => {
    if (state.repeat) {
      handleRepeatOnEnded();
      return;
    }
    quranAudio.next();
  });
  audio.addEventListener('error', () => {
    state.error = 'Erreur de lecture audio.';
    state.isPlaying = false;
    emit();
  });
  audio.addEventListener('play', () => {
    state.isPlaying = true; state.isPaused = false; emit();
  });
  audio.addEventListener('pause', () => {
    state.isPlaying = false; state.isPaused = true; emit();
  });
  return audio;
}

function disposeBlobUrl() {
  if (revoked) {
    try { URL.revokeObjectURL(revoked); } catch {}
    revoked = null;
  }
}

async function resolveTrackSource(track) {
  // 1) Try local cache.
  const localUrl = await audioStorage.getLocalAudioUrl(track.ayahKey, track.reciterId, track.riwaya);
  if (localUrl) return { url: localUrl, isLocal: true };
  // 2) Streaming fallback if explicitly enabled.
  if (state.audioMode === 'offline_first_with_streaming_fallback' && track.remoteUrl) {
    return { url: track.remoteUrl, isLocal: false };
  }
  return null;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

async function handleRepeatOnEnded() {
  const r = state.repeat;
  if (!r) return;
  // Pause between repeats
  if (r.pauseMs > 0) {
    await sleep(r.pauseMs);
    if (!state.repeat) return; // user stopped during pause
  }
  if (r.mode === 'single_ayah') {
    // Repeat the same ayah `repeatCount` times
    r.ayahCounter += 1;
    if (r.ayahCounter < r.repeatCount) {
      await playIndex(state.currentIndex);
      return;
    }
    quranAudio.stop();
    return;
  }
  if (r.mode === 'each_ayah') {
    // Repeat current ayah repeatCount times, then move to next
    r.ayahCounter += 1;
    if (r.ayahCounter < r.repeatCount) {
      await playIndex(state.currentIndex);
      return;
    }
    // Move to next ayah in range
    r.ayahCounter = 0;
    if (state.currentIndex + 1 <= r.endIndex) {
      await playIndex(state.currentIndex + 1);
      return;
    }
    quranAudio.stop();
    return;
  }
  if (r.mode === 'full_range') {
    // Play full range, then repeat the whole range repeatCount times
    if (state.currentIndex + 1 <= r.endIndex) {
      await playIndex(state.currentIndex + 1);
      return;
    }
    r.rangeCounter += 1;
    if (r.rangeCounter < r.repeatCount) {
      await playIndex(r.startIndex);
      return;
    }
    quranAudio.stop();
    return;
  }
  quranAudio.next();
}

async function playIndex(idx) {
  state.currentIndex = idx;
  const track = state.queue[idx];
  state.currentTrack = track || null;
  state.error = null;
  emit();
  if (!track) return;

  const src = await resolveTrackSource(track);
  if (!src) {
    state.isPlaying = false;
    state.error = "Audio local manquant pour ce verset.";
    state.needsDownload = {
      scopeType: 'surah',
      surahNumber: track.surahNumber,
      hizbNumber: track.hizbNumber,
    };
    emit();
    return;
  }
  disposeBlobUrl();
  if (src.isLocal) revoked = src.url;
  ensureAudioElement().src = src.url;
  try {
    await audio.play();
  } catch (err) {
    state.error = "Lecture refusée par le navigateur. Touche play à nouveau.";
    state.isPlaying = false;
    emit();
  }
}

async function buildQueueFromAyahs(ayahs, reciterId) {
  return ayahs.map((a) => quranAudioProvider.buildTrack(a, reciterId));
}

async function startQueue(tracks, mode, startIndex = 0) {
  state.queue = tracks;
  state.mode = mode;
  state.needsDownload = null;
  state.error = null;
  await playIndex(Math.max(0, Math.min(startIndex, tracks.length - 1)));
}

async function checkScopeDownloaded(scope, reciterId, riwaya) {
  // Check the specific scope first (e.g., surah-114)
  const isSpecificDownloaded = await audioStorage.isScopeDownloaded({ ...scope, reciterId, riwaya });
  if (isSpecificDownloaded) return true;

  // If not, check if the FULL QURAN is downloaded — it contains all surah/hizb
  if (scope.scopeType !== 'full_quran') {
    const isFullQuranDownloaded = await audioStorage.isScopeDownloaded({
      scopeType: 'full_quran',
      reciterId,
      riwaya,
    });
    if (isFullQuranDownloaded) return true;
  }

  return false;
}

async function preflightDownload(scope) {
  state.needsDownload = scope;
  state.error = null;
  state.isPlaying = false;
  emit();
}

export const quranAudio = {
  /** React-style subscriber. Returns an unsubscribe fn. */
  subscribe(cb) {
    listeners.add(cb);
    cb({ ...state });
    return () => listeners.delete(cb);
  },

  getState() { return { ...state }; },

  setReciter(reciterId) {
    const reciter = quranAudioProvider.getReciterById(reciterId);
    state.reciterId = reciter.id;
    state.riwaya = reciter.riwaya;
    emit();
  },

  setRiwaya(riwaya) { state.riwaya = riwaya; emit(); },

  setAudioMode(mode) { state.audioMode = mode; emit(); },

  /** Play a single ayah. If its surah is downloaded, start at this verse,
   *  otherwise raise a download prompt for the surah. */
  async playAyah(ayah) {
    const downloaded = await checkScopeDownloaded(
      { scopeType: 'surah', surahNumber: ayah.surahNumber },
      state.reciterId, state.riwaya,
    );
    if (!downloaded) {
      // Even partial downloads can play; check ayah-level presence too.
      const present = await audioStorage.isAyahDownloaded(
        ayah.ayahKey, state.reciterId, state.riwaya,
      );
      if (!present && state.audioMode === 'offline_only') {
        await preflightDownload({
          scopeType: 'surah',
          surahNumber: ayah.surahNumber,
          hizbNumber: ayah.hizbNumber,
        });
        return;
      }
    }
    const ayahs = await getAyahsForSurah(ayah.surahNumber);
    const tracks = await buildQueueFromAyahs(ayahs, state.reciterId);
    const startIdx = ayahs.findIndex((a) => a.ayahKey === ayah.ayahKey);
    await startQueue(tracks, 'ayah', startIdx >= 0 ? startIdx : 0);
  },

  async playSurah(surahNumber) {
    const downloaded = await checkScopeDownloaded(
      { scopeType: 'surah', surahNumber }, state.reciterId, state.riwaya,
    );
    if (!downloaded && state.audioMode === 'offline_only') {
      await preflightDownload({ scopeType: 'surah', surahNumber });
      return;
    }
    const ayahs = await getAyahsForSurah(surahNumber);
    const tracks = await buildQueueFromAyahs(ayahs, state.reciterId);
    await startQueue(tracks, 'surah', 0);
  },

  async playHizb(hizbNumber) {
    const downloaded = await checkScopeDownloaded(
      { scopeType: 'hizb', hizbNumber }, state.reciterId, state.riwaya,
    );
    if (!downloaded && state.audioMode === 'offline_only') {
      await preflightDownload({ scopeType: 'hizb', hizbNumber });
      return;
    }
    const ayahs = await getAyahsForHizb(hizbNumber);
    const tracks = await buildQueueFromAyahs(ayahs, state.reciterId);
    await startQueue(tracks, 'hizb', 0);
  },

  async playFullQuran() {
    const downloaded = await checkScopeDownloaded(
      { scopeType: 'full_quran' }, state.reciterId, state.riwaya,
    );
    if (!downloaded && state.audioMode === 'offline_only') {
      await preflightDownload({ scopeType: 'full_quran' });
      return;
    }
    const ayahs = await getAyahsForFullQuran();
    const tracks = await buildQueueFromAyahs(ayahs, state.reciterId);
    await startQueue(tracks, 'quran', 0);
  },

  /** Memorization scope: not implemented for playback yet, but exposes the
   *  contract so the future memorization module can call it. */
  async playMemorizationRange(scope) {
    return this.playMemorizationRepeat({
      surahNumber: scope.surahNumber,
      fromAyah: scope.fromAyah,
      toAyah: scope.toAyah,
      mode: 'full_range',
      repeatCount: 1,
      pauseMs: 0,
    });
  },

  /**
   * Memorization repetition playback.
   *  - mode: 'single_ayah' | 'each_ayah' | 'full_range'
   *      'single_ayah': replays the verse at `startAyah` `repeatCount` times then stops
   *      'each_ayah': for each ayah from `fromAyah` to `toAyah`, replays it `repeatCount` times
   *      'full_range': plays the whole range, then repeats the whole range `repeatCount` times
   *  - pauseMs: pause between repeats (default 800ms)
   */
  async playMemorizationRepeat({
    surahNumber,
    fromAyah,
    toAyah,
    startAyah = null,
    mode = 'each_ayah',
    repeatCount = 3,
    pauseMs = 800,
  }) {
    if (!surahNumber) return;
    const ayahsInSurah = await getAyahsForSurah(surahNumber);
    if (!ayahsInSurah.length) return;
    const lo = fromAyah || 1;
    const hi = toAyah || ayahsInSurah.length;
    const range = ayahsInSurah.filter(
      (a) => a.ayahNumber >= lo && a.ayahNumber <= hi,
    );
    if (!range.length) return;

    const downloaded = await checkScopeDownloaded(
      { scopeType: 'surah', surahNumber },
      state.reciterId, state.riwaya,
    );
    if (!downloaded && state.audioMode === 'offline_only') {
      // Best-effort: even partial coverage of the range works (per-ayah lookup).
      // Only prompt if no track in the range is locally present.
      let anyPresent = false;
      for (const a of range) {
        // eslint-disable-next-line no-await-in-loop
        if (await audioStorage.isAyahDownloaded(a.ayahKey, state.reciterId, state.riwaya)) {
          anyPresent = true; break;
        }
      }
      if (!anyPresent) {
        await preflightDownload({ scopeType: 'surah', surahNumber });
        return;
      }
    }

    let queue;
    let startIdx;
    if (mode === 'single_ayah') {
      const target = startAyah || lo;
      const single = range.filter((a) => a.ayahNumber === target);
      if (!single.length) return;
      queue = await buildQueueFromAyahs(single, state.reciterId);
      startIdx = 0;
    } else {
      queue = await buildQueueFromAyahs(range, state.reciterId);
      startIdx = 0;
    }

    state.repeat = {
      mode,
      repeatCount: Math.max(1, Number(repeatCount) || 1),
      pauseMs: Math.max(0, Number(pauseMs) || 0),
      ayahCounter: 0,
      rangeCounter: 0,
      startIndex: 0,
      endIndex: queue.length - 1,
    };
    await startQueue(queue, 'memorization', startIdx);
  },

  pause() { ensureAudioElement().pause(); },

  resume() {
    const a = ensureAudioElement();
    if (a.src) a.play().catch(() => {});
  },

  stop() {
    if (audio) { audio.pause(); audio.removeAttribute('src'); audio.load(); }
    state.isPlaying = false; state.isPaused = false;
    state.currentTrack = null; state.queue = []; state.mode = null;
    state.progress = 0; state.duration = 0; state.error = null;
    state.repeat = null;
    disposeBlobUrl();
    emit();
  },

  next() {
    if (state.currentIndex + 1 >= state.queue.length) {
      this.stop();
      return;
    }
    playIndex(state.currentIndex + 1);
  },

  previous() {
    if (state.currentIndex <= 0) {
      // Restart current track
      if (audio) audio.currentTime = 0;
      return;
    }
    playIndex(state.currentIndex - 1);
  },

  seek(seconds) {
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(seconds, audio.duration || seconds));
  },

  /** Called by UI when user accepts the download prompt. */
  clearNeedsDownload() {
    state.needsDownload = null;
    emit();
  },
};
